import React, { useState, useEffect, useCallback } from 'react';
import { useFiiDiiData } from '../../hooks/useFiiDiiData';
import { useMutualFundData } from '../../hooks/useMutualFundData';
import { formatCr } from '../../services/fiiDiiService';
import FiiDiiExplorerModal from '../modals/FiiDiiExplorerModal';
import MutualFundFlowModal from '../modals/MutualFundFlowModal';
import '../../styles/fiiDii.css';

export const FiiDiiHeaderTicker: React.FC = () => {
  const {
    latest,
    history,
    monthly,
    loadingDetails,
    loadFullDetails,
    refresh,
  } = useFiiDiiData();

  const mf = useMutualFundData();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMfModalOpen, setIsMfModalOpen] = useState(false);

  const handleOpenExplorer = useCallback(() => {
    setIsModalOpen(true);
    void loadFullDetails();
  }, [loadFullDetails]);

  const handleOpenMfExplorer = useCallback(() => {
    setIsMfModalOpen(true);
    void mf.loadFullDetails();
  }, [mf]);

  useEffect(() => {
    const handleFiiEvent = () => handleOpenExplorer();
    const handleMfEvent = () => handleOpenMfExplorer();

    window.addEventListener('open-fii-dii-modal', handleFiiEvent);
    window.addEventListener('open-mutual-fund-modal', handleMfEvent);
    return () => {
      window.removeEventListener('open-fii-dii-modal', handleFiiEvent);
      window.removeEventListener('open-mutual-fund-modal', handleMfEvent);
    };
  }, [handleOpenExplorer, handleOpenMfExplorer]);

  if (!latest && !isModalOpen && !isMfModalOpen) {
    return null;
  }

  const isFiiPos = (latest?.fii?.net ?? 0) >= 0;
  const isDiiPos = (latest?.dii?.net ?? 0) >= 0;
  const isCombPos = (latest?.combinedNet ?? 0) >= 0;

  return (
    <>
      {latest && (
        <div className="ns-fiidii-ticker-container" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <div
          className="ns-fiidii-ticker"
          onClick={handleOpenExplorer}
          role="button"
          tabIndex={0}
          aria-label="Institutional Cash Market Flows. Click to explore 14-year history."
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleOpenExplorer();
            }
          }}
        >
        <span className="ns-fiidii-ticker__pulse" />

        {/* Compact View on standard screens (< 1536px) */}
        <span className="ns-fiidii-ticker__compact">
          <span className="ns-fiidii-ticker__label">Cash:</span>
          <span
            className={
              isCombPos ? 'ns-fiidii-ticker__value--pos' : 'ns-fiidii-ticker__value--neg'
            }
          >
            {formatCr(latest.combinedNet, true)}
          </span>
        </span>

        {/* Full Breakdown View on ultra-wide screens (>= 1536px) */}
        <span className="ns-fiidii-ticker__full">
          {/* FII Segment */}
          <span className="ns-fiidii-ticker__segment">
            <span className="ns-fiidii-ticker__label">FII:</span>
            <span
              className={
                isFiiPos ? 'ns-fiidii-ticker__value--pos' : 'ns-fiidii-ticker__value--neg'
              }
            >
              {formatCr(latest.fii.net, true)}
            </span>
          </span>

          <span className="ns-fiidii-ticker__divider">|</span>

          {/* DII Segment */}
          <span className="ns-fiidii-ticker__segment">
            <span className="ns-fiidii-ticker__label">DII:</span>
            <span
              className={
                isDiiPos ? 'ns-fiidii-ticker__value--pos' : 'ns-fiidii-ticker__value--neg'
              }
            >
              {formatCr(latest.dii.net, true)}
            </span>
          </span>

          <span className="ns-fiidii-ticker__divider">|</span>

          {/* Combined Net Segment */}
          <span className="ns-fiidii-ticker__segment">
            <span className="ns-fiidii-ticker__label">Net:</span>
            <span
              className={
                isCombPos ? 'ns-fiidii-ticker__value--pos' : 'ns-fiidii-ticker__value--neg'
              }
            >
              {formatCr(latest.combinedNet, true)}
            </span>
          </span>
        </span>

        {/* Hover Tooltip Breakdown */}
        <div className="ns-fiidii-tooltip">
          <div className="ns-fiidii-tooltip__header">
            <span className="ns-fiidii-tooltip__title">🏛️ Institutional Pulse</span>
            <span className="ns-fiidii-tooltip__date">{latest.displayDate}</span>
          </div>

          {/* FII */}
          <div className="ns-fiidii-tooltip__row">
            <span>FII / FPI Net</span>
            <strong className={isFiiPos ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
              {formatCr(latest.fii.net, true)}
            </strong>
          </div>
          <div className="ns-fiidii-tooltip__subrow">
            <span>Gross Buy: {formatCr(latest.fii.buy)}</span>
            <span>Gross Sell: {formatCr(latest.fii.sell)}</span>
          </div>

          {/* DII */}
          <div className="ns-fiidii-tooltip__row" style={{ marginTop: '4px' }}>
            <span>DII Net</span>
            <strong className={isDiiPos ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
              {formatCr(latest.dii.net, true)}
            </strong>
          </div>
          <div className="ns-fiidii-tooltip__subrow">
            <span>Gross Buy: {formatCr(latest.dii.buy)}</span>
            <span>Gross Sell: {formatCr(latest.dii.sell)}</span>
          </div>

          {/* Total Net */}
          <div
            className="ns-fiidii-tooltip__row"
            style={{
              marginTop: '6px',
              paddingTop: '6px',
              borderTop: '1px solid var(--ns-border-subtle, rgba(255, 255, 255, 0.08))',
            }}
          >
            <span style={{ fontWeight: 600 }}>Combined Net Flow</span>
            <strong className={isCombPos ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
              {formatCr(latest.combinedNet, true)}
            </strong>
          </div>

          {/* Pure MF & SIP Macro Preview */}
          {mf.latest && (
            <div
              className="ns-fiidii-tooltip__row"
              style={{
                marginTop: '6px',
                paddingTop: '6px',
                borderTop: '1px solid var(--ns-border-subtle, rgba(255, 255, 255, 0.08))',
                fontSize: '11px',
              }}
            >
              <span>Pure MF Equity: {formatCr(mf.latest.equityNet, true)}</span>
              <span style={{ color: '#818cf8', fontWeight: 600 }}>
                SIP: {formatCr(mf.latest.latestMonthSipCr)}/mo
              </span>
            </div>
          )}

          <div
            className="ns-fiidii-tooltip__footer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleOpenExplorer();
              }}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              FII/DII (14-Yr) ↗
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleOpenMfExplorer();
              }}
              style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--ns-brand, #6366f1)' }}
            >
              📈 MF &amp; SIPs (5-Yr) ↗
            </span>
          </div>
        </div>
      </div>

      {/* Dedicated Mutual Fund & SIP Inflows Header Pill */}
      <div
        className="ns-fiidii-ticker"
        onClick={handleOpenMfExplorer}
        role="button"
        tabIndex={0}
        title="Mutual Fund Flows & 5-Year SIP Super-Cycle (Click to open)"
        aria-label="Mutual Fund Flows & 5-Year SIP Super-Cycle. Click to explore 5-year AMFI history."
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOpenMfExplorer();
          }
        }}
        style={{
          borderColor: 'rgba(99, 102, 241, 0.35)',
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#6366f1',
            display: 'inline-block',
            boxShadow: '0 0 6px rgba(99, 102, 241, 0.6)',
            marginRight: '2px',
          }}
        />
        <span className="ns-fiidii-ticker__label" style={{ color: 'var(--ns-brand, #6366f1)', fontWeight: 600 }}>
          SIP:
        </span>
        <span style={{ fontWeight: 700 }}>
          {mf.latest?.latestMonthSipCr
            ? `₹${Math.round(mf.latest.latestMonthSipCr).toLocaleString('en-IN')} Cr`
            : '₹32,490 Cr'}
        </span>
      </div>
    </div>
      )}

      {/* FII/DII Explorer Modal */}
      <FiiDiiExplorerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        latest={latest}
        history={history}
        monthly={monthly}
        loadingDetails={loadingDetails}
        onRefresh={refresh}
        onSwitchToMutualFunds={() => {
          setIsModalOpen(false);
          setIsMfModalOpen(true);
          void mf.loadFullDetails();
        }}
      />

      {/* Mutual Fund & SIP Super-Cycle Modal */}
      <MutualFundFlowModal
        isOpen={isMfModalOpen}
        onClose={() => setIsMfModalOpen(false)}
        latest={mf.latest}
        history={mf.history}
        monthly={mf.monthly}
        loadingDetails={mf.loadingDetails}
        onRefresh={mf.refresh}
        onSwitchToFiiDii={() => {
          setIsMfModalOpen(false);
          setIsModalOpen(true);
          void loadFullDetails();
        }}
      />
    </>
  );
};

export default FiiDiiHeaderTicker;
